import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { LoadingSpinner } from '../../ui/loading-spinner';
import { Search, MapPin, Building, Calendar } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '../../ui/theme-provider';
import { cn } from '../../../lib/utils';

const RSSJobSelector = ({ onJobSelect, selectedJobId }) => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [fullJobs, setFullJobs] = useState(null);
  const [fullLoaded, setFullLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const { toast } = useToast();
  const { theme } = useTheme();

  useEffect(() => {
    // initial load: fetch the most recent jobs (limited)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchRSSJobs(false);
  }, []);

  // When the user types a search term we should search the entire collection
  // rather than only the limited recent set. Use a small debounce so we don't
  // hammer Firestore on every keystroke.
  useEffect(() => {
    const term = searchTerm.trim();
    const timer = setTimeout(() => {
      // Only trigger a search/fetch when the user has typed >= 3 chars
      // or when they clear the field (restore defaults). This prevents
      // hitting Firestore on every keystroke.
      if (term.length >= 3) {
        // If we already loaded the full jobs list, avoid refetching
        if (fullLoaded && fullJobs) {
          // apply client-side filter on cached fullJobs
          setJobs(fullJobs);
        } else {
          // load capped full set (handled inside fetchRSSJobs)
          fetchRSSJobs(true);
        }
      } else if (term === '') {
        // restore default limited recent list
        fetchRSSJobs(false);
      }
      // otherwise (1-2 chars) do nothing until Enter or 3+ chars
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    // Filter jobs based on search term and job type
    let filtered = jobs;
    
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(job => 
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (jobTypeFilter !== 'all') {
      filtered = filtered.filter(job => {
        const title = job.title?.toLowerCase() || '';
        switch(jobTypeFilter) {
          case 'hygienist':
            return title.includes('hygienist') || title.includes('dental hygienist');
          case 'dentist':
            return title.includes('dentist') && !title.includes('hygienist');
          case 'nurse':
            return title.includes('nurse') || title.includes('dental nurse');
          case 'therapist':
            return title.includes('therapist') || title.includes('dental therapist');
          default:
            return true;
        }
      });
    }
    
    setFilteredJobs(filtered);
  }, [searchTerm, jobs, jobTypeFilter]);

  const fetchRSSJobs = async (fetchAll = false) => {
    try {
      setLoading(true);
      
      console.log('Fetching RSS jobs from collection: rss_jobs');
      console.log('Firebase db instance:', db);
      
      // Try simple collection fetch first
      const collectionRef = collection(db, 'rss_jobs');
      console.log('Collection reference:', collectionRef);
      
  // If fetchAll is false we will still fetch all docs but only keep the
  // most recent 20. Firestore doesn't support a cheap OR across multiple
  // fields for text search, so we load the documents and filter client-side.
  const querySnapshot = await getDocs(collectionRef);
      console.log('Query snapshot size:', querySnapshot.size);
      
      const jobsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // normalize postedDate field - some imports use 'postedAt' or 'postedDate'
        if (!data.postedDate && data.postedAt) {
          data.postedDate = data.postedAt;
        }
        console.log('Job data sample:', { 
          id: doc.id, 
          title: data.title, 
          company: data.company, 
          postedDate: data.postedDate,
          postedDateType: typeof data.postedDate,
          postedDateValue: data.postedDate
        });
        return {
          id: doc.id,
          ...data
        };
      });

      // Sort jobs by posted date if available (most recent first)
      jobsData.sort((a, b) => {
        let dateA, dateB;
        
        try {
          if (a.postedDate?.toDate) {
            dateA = a.postedDate.toDate();
          } else if (a.postedDate?.seconds) {
            dateA = new Date(a.postedDate.seconds * 1000);
          } else if (a.postedDate) {
            dateA = new Date(a.postedDate);
          } else {
            dateA = new Date(0); // Very old date for jobs without dates
          }
        } catch (e) {
          dateA = new Date(0);
        }
        
        try {
          if (b.postedDate?.toDate) {
            dateB = b.postedDate.toDate();
          } else if (b.postedDate?.seconds) {
            dateB = new Date(b.postedDate.seconds * 1000);
          } else if (b.postedDate) {
            dateB = new Date(b.postedDate);
          } else {
            dateB = new Date(0);
          }
        } catch (e) {
          dateB = new Date(0);
        }
        
        return dateB - dateA; // Most recent first
      });

      let resultJobs = jobsData;
      if (!fetchAll) {
        // default: limit to 20 most recent for the selector
        resultJobs = jobsData.slice(0, 20);
      } else {
        // when searching, cap the fetched set to a reasonable maximum (200)
        resultJobs = jobsData.slice(0, 200);
        // cache the fetched full set so subsequent searches use the local copy
        setFullJobs(resultJobs);
        setFullLoaded(true);
      }

      console.log('Total jobs fetched (after limit):', resultJobs.length);
      setJobs(resultJobs);
      setFilteredJobs(resultJobs);

  // If nothing is currently selected, auto-select the latest job (first in list)
      try {
        if (resultJobs.length > 0 && !selectedJobId && typeof onJobSelect === 'function') {
          onJobSelect(resultJobs[0]);
        }
      } catch (e) {
        console.warn('Auto-select latest job failed:', e);
      }

      if (resultJobs.length === 0) {
        toast({
          title: "No jobs found",
          description: "No RSS jobs are currently available in the database.",
          variant: "warning"
        });
      } else {
        toast({
          title: "Jobs Loaded",
          description: `Successfully loaded ${resultJobs.length} RSS jobs.`,
          variant: "success"
        });
      }

    } catch (error) {
      console.error('Error fetching RSS jobs:', error);
      toast({
        title: "Error",
        description: `Failed to load RSS jobs: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleJobSelect = (job) => {
    onJobSelect(job);
    toast({
      title: "Job Selected",
      description: `Selected ${job.title} at ${job.company}`,
      variant: "success"
    });
  };

  const formatPostedDate = (date) => {
    if (!date) return 'Unknown date';
    
    try {
      let dateObj;
      
      // Handle different date formats
      if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp
        dateObj = date.toDate();
      } else if (date.seconds) {
        // Firestore Timestamp object format
        dateObj = new Date(date.seconds * 1000);
      } else if (typeof date === 'string') {
        // String date
        dateObj = new Date(date);
      } else if (date instanceof Date) {
        // Already a Date object
        dateObj = date;
      } else {
        console.log('Unknown date format:', date, typeof date);
        return 'Unknown date';
      }
      
      // Validate the date
      if (isNaN(dateObj.getTime())) {
        console.log('Invalid date:', date);
        return 'Invalid date';
      }
      
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch (error) {
      console.log('Date formatting error:', error, 'for date:', date);
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <LoadingSpinner />
          <p className="mt-4 text-muted-foreground">Loading RSS jobs...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Header */}
      <Card>
        <CardHeader>
          <CardTitle className={cn(
            "text-lg flex items-center gap-2",
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}>
            <Search className="h-5 w-5" />
            RSS Job Selection
          </CardTitle>
          
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search jobs, companies, locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const term = searchTerm.trim();
                    if (term.length >= 1) {
                      // ensure we have a search set loaded; if not, fetch capped full list
                      if (!fullLoaded) fetchRSSJobs(true);
                      else setJobs(fullJobs || []);
                    }
                  }
                }}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-3">
              <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filter by job type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Jobs</SelectItem>
                  <SelectItem value="hygienist">Dental Hygienist</SelectItem>
                  <SelectItem value="dentist">Dentist</SelectItem>
                  <SelectItem value="nurse">Dental Nurse</SelectItem>
                  <SelectItem value="therapist">Dental Therapist</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className={cn(
            "text-sm",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            {filteredJobs.length} jobs available
          </div>
        </CardHeader>
      </Card>

      {/* Jobs List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No jobs found</h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms' : 'No RSS jobs are currently available'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredJobs.map((job) => (
            <Card 
              key={job.id} 
              className={cn(
                "cursor-pointer transition-all duration-200 hover:shadow-lg border-l-4",
                selectedJobId === job.id 
                  ? 'ring-2 ring-primary border-l-primary bg-primary/5' 
                  : 'border-l-transparent hover:border-l-primary/30',
                theme === 'light' ? 'bg-white' : 'bg-card'
              )}
              onClick={() => handleJobSelect(job)}
            >
              <CardContent className="p-3"> {/* Reduced padding */}
                <div className="space-y-2"> {/* Reduced spacing */}
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <h3 className={cn(
                        "font-semibold text-sm leading-tight mb-2 truncate", 
                        theme === 'light' ? 'text-gray-900' : 'text-white'
                      )} title={job.title}>
                        {job.title || 'Untitled Job'}
                      </h3>
                      
                      {/* Company and Location in a single row */}
                      <div className="flex items-center gap-4 text-sm">
                        <div className={cn(
                          "flex items-center gap-1.5", 
                          theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
                        )}>
                          <Building className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="font-medium">
                            {(() => {
                              // Extract company name from title if job.company is not available or generic
                              if (!job.company || job.company === 'Company not specified') {
                                if (job.title && job.title.includes(':')) {
                                  const titleParts = job.title.split(':');
                                  return titleParts[0].trim();
                                }
                              }
                              return job.company || 'Company not specified';
                            })()}
                          </span>
                        </div>
                        
                        <div className={cn(
                          "flex items-center gap-1.5", 
                          theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
                        )}>
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{job.location || 'Location not specified'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-4"> {/* Reduced gap */}
                      <Badge variant="outline" className="text-xs">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatPostedDate(job.postedDate)}
                      </Badge>
                      {job.salary && (
                        <Badge variant="secondary" className="text-xs">
                          {job.salary}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Removed description to save space */}
                  
                  <div className="flex justify-between items-center pt-1"> {/* Reduced padding */}
                    <div className="flex gap-1">
                      {job.employmentType && (
                        <Badge variant="outline" className="text-xs">
                          {job.employmentType}
                        </Badge>
                      )}
                    </div>
                    <Button 
                      onClick={() => handleJobSelect(job)}
                      size="sm"
                      variant={selectedJobId === job.id ? "default" : "outline"}
                      className="text-xs px-3 py-1" /* Smaller button */
                    >
                      {selectedJobId === job.id ? 'Selected' : 'Use This Job'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {filteredJobs.length > 0 && (
        <Card>
          <CardContent className="p-4 text-center">
            <Button onClick={fetchRSSJobs} variant="outline" size="sm">
              Refresh Jobs
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RSSJobSelector;
